import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withFallbackCallout } from '@bingads-webui-react/fallback-feature';
import { StyledMenuItemTag } from '@bingads-webui-campaign-react/shell-ui-common';
import { getPageMenuItemStyles } from '../styles/page-menu-item-styles';
import LandingPageSelector from './landing-page-selector';

function getPageMenuItemText(classes, pageItem) {
  return (
    <span className={classNames([classes.text, 'page-menu-item-content'])} >
      {pageItem.content}
    </span>
  );
}

function onClick() {
  // disable clicking on links, only allow to open in new tabs/windows
  return false;
}

const PageMenuItemText = ({
  classes, pageItem, pageItemUrl, showLabel,
}) => (
  <div
    className={classNames([classes.textContainer, 'page-menu-item-text-container'])}
    title={showLabel ? pageItem.content : ''}
  >
    {pageItemUrl ? (
      <a href={pageItemUrl} onClick={onClick}>
        {getPageMenuItemText(classes, pageItem)}
      </a>
    ) : (
      getPageMenuItemText(classes, pageItem)
    )}
    {pageItem.showTag ? // preserve this check for better performance
      <div className={classes.menuItemTagContainer}>
        <StyledMenuItemTag tagContent={pageItem.showTag} tagClass={pageItem.tagClass} key="page-menu-item-tag" i18n={pageItem.i18n} />
      </div> : ''}
  </div>

);

const PageMenuItem = ({
  classes,
  pageItem,
  setLandingPage,
  pageItemUrl = '',
  withRelationLine = false,
  showLabel = false,
  renderIcon = null,
  collapsed = false,
}) => {
  const classNameArray = [
    classes.baseMenuItem,
    pageItem.isSelected
      ? [classes.selectedPageMenuItem, 'page-menu-item-selected']
      : classes.pageMenuItem,
    `bingads-campaign-react-page-menu-item-${pageItem.id}`,
    'bingads-campaign-react-page-menu-item',
    `bingads-campaign-react-page-menu-item-${withRelationLine ? 'sub' : 'top'}`,
  ];

  if (!pageItem.isPageEnabled) {
    classNameArray.push(classes.notEnabledPageMenuItem);

    if (pageItem.isFallbackSelected) {
      classNameArray.push(classes.notEnabledSelectedPageMenuItem);
    }
  }

  const className = classNames(classNameArray);

  return (
    <div
      key={`wrapper-div-${pageItem.id}`}
      className={className}
      aria-label={pageItem.content}
      role="link"
    >
      {withRelationLine && <div className={classes.relationLine} />}
      {pageItem.isSelected && <div className={classes.beforeIcon} />}
      {renderIcon && <div className={classes.iconContainer}>{renderIcon(pageItem)}</div>}
      {(!renderIcon || !collapsed) && <PageMenuItemText classes={classes} pageItem={pageItem} pageItemUrl={pageItemUrl} showLabel={showLabel} />}
      <LandingPageSelector
        isPageEnabled={pageItem.isPageEnabled}
        isDefaultLandingPage={pageItem.isDefaultLandingPage}
        pageId={pageItem.id}
        setLandingPage={setLandingPage}
        defaultLandingPageTooltipText={pageItem.defaultLandingPageTooltipText}
      />
    </div>
  );
};

PageMenuItem.defaultProps = {
  setLandingPage: null,
  pageItemUrl: '',
  withRelationLine: false,
  showLabel: false,
  renderIcon: null,
  collapsed: false,
};

PageMenuItemText.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getPageMenuItemStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  pageItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    isDefaultLandingPage: PropTypes.bool,
  }).isRequired,
  pageItemUrl: PropTypes.string.isRequired,
  showLabel: PropTypes.bool.isRequired,
};

PageMenuItem.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getPageMenuItemStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  pageItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    isDefaultLandingPage: PropTypes.bool,
    isSelected: PropTypes.bool.isRequired,
    isPageEnabled: PropTypes.bool.isRequired,
    isFallbackSelected: PropTypes.bool.isRequired,
    defaultLandingPageTooltipText: PropTypes.string,
    showTag: PropTypes.string,
    tagClass: PropTypes.string,
  }).isRequired,
  setLandingPage: PropTypes.func,
  pageItemUrl: PropTypes.string,
  withRelationLine: PropTypes.bool,
  showLabel: PropTypes.bool,
  renderIcon: PropTypes.func,
  collapsed: PropTypes.bool,
};

export default withDefaultStyles(withFallbackCallout(PageMenuItem), getPageMenuItemStyles);
